import { createRoot } from "react-dom/client";
import App from "./components/App";
import { legacy_createStore as createStore, applyMiddleware } from "redux";
import { Provider } from "react-redux";
import rootReducer from "./reducers";
import { thunk } from "redux-thunk";
import { composeWithDevTools } from "@redux-devtools/extension";
import { persistStore } from "redux-persist";
import { PersistGate } from "redux-persist/lib/integration/react";
import {
  ThemeProvider,
  StyledEngineProvider,
  createTheme,
} from "@mui/material/styles";
import { alertClasses } from "@mui/material";

const theme = createTheme({
  palette: {
    primary: {
      main: "#3F51B5",
    },
    secondary: {
      main: "#F50057",
    },
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        // Hide all Mui* controls when printing.
        // Anything that wants to print needs to override this.
        // (e.g. Map.jsx, ScaleGrid.jsx)
        "@media print": {
          '[class*="Mui"]': {
            visibility: "hidden",
          },
        },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: {
          // Replaces old `filledInfo`
          [`&.${alertClasses.filled}.${alertClasses.colorInfo}`]: {
            backgroundColor: "#016ca7",
            color: "#fff",
          },
          // Replaces old `filledWarning`
          [`&.${alertClasses.filled}.${alertClasses.colorWarning}`]: {
            backgroundColor: "#b45201",
            color: "#fff",
          },
        },
      },
    },
    MuiGrid: {
      styleOverrides: {
        root: {
          // https://mui.com/material-ui/migration/upgrade-to-grid-v2/#container-width
          //
          // When moving to the new Grid component, containers no longer grow by default.
          // Unfortunately, there are two ways to address this depending on whether this
          // is inside a flex ancestor or not.
          //
          // Here we assume flex and apply the suggested fix globally, but ONLY to
          // <Grid's with the container attribute.  We'll then deal with the non-flex
          // case on an as-needed basis using local stying.
          "&.MuiGrid-container": {
            flexGrow: 1,
          },
        },
      },
    },
    MuiTooltip: {
      defaultProps: {
        // Material UI v7 changed tooltips so that they stay around and
        // the user can click on them and select their text.
        //
        // However, for many of our components (e.g. drawing), this causes
        // the tooltips to obscure nearby buttons making them impossible to
        // be clicked until the tooltip is gone.  Since this is annoying, we
        // change the tooltip behavior back to what it was.
        //
        // We could painstakingly rework each tooltip to be positioned to not
        // obscure nearby buttons but that would be fragile in things change.
        disableInteractive: true,
      },
    },
  },
});

/*
Uncomment to allow redux devtool trace
 https://github.com/zalmoxisus/redux-devtools-extension/blob/master/docs/Features/Trace.md
*/
// const composeEnhancers = composeWithDevTools({
//   trace: true,
// });

export const store = createStore(
  rootReducer,
  /* Default: Comment out to use redux devtools trace */
  composeWithDevTools(applyMiddleware(thunk)),
  /* LOCAL DEV ONLY: Uncomment to allow redux devtool trace */
  // composeEnhancers(
  //     applyMiddleware(thunk),
  // )
);

// Used only for automated testing (playwright).
window.mgis_store = store;

const persistor = persistStore(store);

const container = document.getElementById("root");
const root = createRoot(container);

root.render(
  <Provider store={store}>
    <PersistGate loading={null} persistor={persistor}>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <App />
        </ThemeProvider>
      </StyledEngineProvider>
    </PersistGate>
  </Provider>,
);
